import { APP_ROUTES, useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { FiArrowRight } from "@payping/icons";
import { dateFormats, moment } from "@payping/utils/dateUtils";
import {
  Button,
  Card,
  Divider,
  Drawer,
  Flex,
  Input,
  InputNumber,
  LegacyForm,
  Radio,
  Typography,
  notification,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { useEffect, useState } from "react";
import { ValidCardNumber, ValidEmail, ValidPostalCode } from "@payping/utils/validation";
import { bnplConsumerApis } from "@payping/api";
import type { FillProfileRequest, SDKRequestInit } from "@payping/api";
import { bnplConsumerGetProfile } from "@payping/redux/actions";
import DrawerLayout from "@/Components/Layout/Drawer";
import { useNavigate } from "react-router";

type FormItems = {
  sex: string;
  cardNumber: string;
  email: string;
  address: string;
  postalCode: string;
};

const HomeProfileAccountPage = () => {
  const { t } = useAppTranslation();
  const profile = useAppSelector((state) => state.bnplConsumer.profile);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [notif, contextHolder] = notification.useNotification({ top: 16, duration: 3, placement: "top" });

  const [form] = LegacyForm.useForm<FormItems>();

  const { retry } = useMessagedAsyncRetry(() => dispatch(bnplConsumerGetProfile()), [dispatch], {
    success: false,
    error: true,
  });

  const [saveProfileState, saveProfile] = useMessagedAsyncFn(
    async (body: FillProfileRequest) => {
      try {
        const res = await bnplConsumerApis.fillProfile(body, { isRaw: true } as SDKRequestInit);
        retry();
        return res;
      } catch (e: any) {
        // Check if e is an instance of Response and if the status is 400
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e; // Fallback if parsing JSON fails
          }
        }

        throw e;
      }
    },
    [retry],
  );

  const [open, setOpen] = useState(true);

  useEffect(() => {
    const errors = (saveProfileState.error as any)?.errors;
    if (errors && errors instanceof Array) {
      errors.forEach((item) => {
        const message = item?.message;
        const code = item?.code;
        if (!message || !code) return;
        notif.error({ message, key: code });
      });
    }
  }, [notif, saveProfileState.error]);

  const handleCloseDrawer = () => {
    if (saveProfileState.loading) return;
    setOpen(false);
    navigate(APP_ROUTES.home.profile.root.url);
  };

  const handleSubmitEditForm = async (data: FormItems) => {
    saveProfile({
      firstNameEnglish: profile.firstNameEnglish ?? "",
      lastNameEnglish: profile.lastNameEnglish ?? "",
      cityEnglish: profile.cityEnglish ?? "",
      provinceEnglish: profile.provinceEnglish ?? "",
      nationalIdSeries: profile.nationalIdSeries ?? "",
      address: data.address,
      email: data.email,
      postalCode: data.postalCode,
      cardNumber: String(data.cardNumber),
      isMale: data.sex === "0",
    });
  };

  return (
    <>
      <Drawer
        open={open}
        title={
          <Flex align="center" gap={"small"}>
            <Button icon={<FiArrowRight width={14} height={14} />} type="text" onClick={handleCloseDrawer} />
            <Typography.Text>{t("common.accountInfo")}</Typography.Text>
          </Flex>
        }
        onClose={handleCloseDrawer}
        size={"100%"}
        placement={"bottom"}
        closeIcon={false}
        mask={false}
        classNames={{
          header: "tw:py-3! tw:px-4!",
          body: "tw:p-0!",
          content: "tw:rounded-md!",
        }}
        getContainer={false}
      >
        <DrawerLayout className="tw:h-full">
          <Flex vertical className="tw:h-full tw:w-full">
            <Flex className="tw:w-full tw:px-6! tw:pt-8!" justify="center" gap={"middle"} vertical align="center">
              <Card size="small" className="tw:w-full">
                <Flex vertical gap={"middle"} className="tw:w-full">
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">{t("common.firstName")}</Typography.Text>
                    <Typography.Text>{profile.firstName ?? ""}</Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">{t("common.lastName")}</Typography.Text>
                    <Typography.Text>{profile.lastName ?? ""}</Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">{t("common.nationalCode")}</Typography.Text>
                    <Typography.Text>{profile.nationalCode ?? ""}</Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">{t("common.dateOfBirth")}</Typography.Text>
                    <Typography.Text>
                      {profile.birthDate ? moment(profile.birthDate).format(dateFormats.ui.jalaliDate) : ""}
                    </Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">{t("common.nationalIdSeriesOrTrackingCode")}</Typography.Text>
                    <Typography.Text>{profile.nationalIdSeries ?? ""}</Typography.Text>
                  </Flex>
                </Flex>
              </Card>
              <LegacyForm<FormItems>
                className="tw:w-full"
                layout="vertical"
                form={form}
                onFinish={handleSubmitEditForm}
                initialValues={
                  {
                    address: profile.address ?? "",
                    cardNumber: profile.cardNumber ?? "",
                    email: profile.email ?? "",
                    postalCode: profile.postalCode ?? "",
                    sex: profile.isMale ? "0" : "1",
                  } satisfies FormItems
                }
              >
                <LegacyForm.Item<FormItems> label={t("common.sex")} name="sex" rules={[{ required: true }]}>
                  <Radio.Group
                    options={[
                      { label: t("common.male"), value: "0" },
                      { label: t("common.female"), value: "1" },
                    ]}
                  />
                </LegacyForm.Item>
                <LegacyForm.Item<FormItems>
                  rules={[{ required: true }, { max: 180, message: t("formValidate.max", { max: 180 }) }]}
                  name={"address"}
                  label={t("pay.getOrder.steps.fillProfile.fillForm.address")}
                >
                  <Input.TextArea
                    maxLength={180}
                    placeholder={t("pay.getOrder.steps.fillProfile.fillForm.addressPlaceholder")}
                  />
                </LegacyForm.Item>
                <LegacyForm.Item<FormItems>
                  rules={[
                    { required: true },
                    {
                      validator(_, emailValue) {
                        if (ValidPostalCode(emailValue)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            t("formValidate.invalid", {
                              name: t("pay.getOrder.steps.fillProfile.fillForm.postalCode"),
                            }),
                          ),
                        );
                      },
                    },
                  ]}
                  name={"postalCode"}
                  label={t("pay.getOrder.steps.fillProfile.fillForm.postalCode")}
                >
                  <Input />
                </LegacyForm.Item>
                <LegacyForm.Item<FormItems>
                  rules={[
                    { required: true },
                    {
                      validator(_, emailValue) {
                        if (ValidEmail(emailValue)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t("formValidate.invalidEmail")));
                      },
                    },
                  ]}
                  name={"email"}
                  label={t("pay.getOrder.steps.fillProfile.fillForm.email")}
                >
                  <Input />
                </LegacyForm.Item>
                <LegacyForm.Item<FormItems>
                  rules={[
                    { required: true },
                    {
                      validator(_, cardNumber) {
                        if (String(cardNumber).length !== 16) {
                          return Promise.reject(new Error(t("formValidate.cardNumberLength", { count: 16 })));
                        }
                        if (!ValidCardNumber(cardNumber.toString())) {
                          return Promise.reject(new Error(t("formValidate.invalidCardNumber")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  name={"cardNumber"}
                  label={t("pay.getOrder.steps.fillProfile.fillForm.cardNumber")}
                >
                  <InputNumber
                    inputMode="numeric"
                    changeOnWheel={false}
                    controls={false}
                    className="tw:w-full!"
                    placeholder={t("pay.getOrder.steps.fillProfile.fillForm.cardNumberPlaceholder")}
                  />
                </LegacyForm.Item>
              </LegacyForm>
            </Flex>
            <Flex className="tw:mt-auto" vertical>
              <Divider className="tw:m-0!" />
              <Flex className="tw:p-4!" gap={"middle"}>
                <Button loading={saveProfileState.loading} block type="primary" onClick={() => form.submit()}>
                  {t("common.save")}
                </Button>
                <Button disabled={saveProfileState.loading} block type="default" onClick={handleCloseDrawer}>
                  {t("common.close")}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </DrawerLayout>
      </Drawer>
      {contextHolder}
    </>
  );
};

export default HomeProfileAccountPage;
